import groupBy from "lodash/groupBy";
import moment from "moment";
import { DATE_FORMATS } from "../../constants";
import { buildPrExerciseKeysByDay } from "../../utils/workout-stats";
import {
  expandLoggedFemaleCycleDays,
  getNormalizedLogs,
  getPredictedCycles,
} from "../../views/athlete-portal/calendar/menstrual-cycle/menstrual-cycle.helper";

const { DATE_DAY_FORMAT } = DATE_FORMATS;
const PREDICTED_CYCLE_MONTHS_AHEAD = 2;

export const buildWeekDays = (selectedWeek) => {
  const weekStart = moment(selectedWeek).startOf("day");
  return Array.from({ length: 7 }, (_, i) => {
    const day = weekStart.clone().add(i, "days");
    return {
      day,
      dayKey: day.format(DATE_DAY_FORMAT),
      isCurrentMonth: true,
      isToday: day.isSame(moment(), "day"),
    };
  });
};

const toDateKey = (item) =>
  moment.utc(item.utcDate?.toString() || item.date).format(DATE_DAY_FORMAT);

// Same loop as calendar-month.controller.js's plan branch: merge predicted
// (forecast) cycle days in on top of the logged ones, skipping any date a
// real log already covers.
const buildFemaleCycleDaysWithPredictions = (data, monthKey) => {
  const normalizedLogs = getNormalizedLogs(
    data.femaleCycleDays || [],
    data.femaleCycleSettings || null
  );
  const expandedCycleDays = expandLoggedFemaleCycleDays(
    normalizedLogs,
    DATE_DAY_FORMAT
  );
  const predictedCycles = data.femaleCycleSettings
    ? getPredictedCycles(
        normalizedLogs,
        data.femaleCycleSettings,
        monthKey,
        PREDICTED_CYCLE_MONTHS_AHEAD
      )
    : [];

  for (const pred of predictedCycles) {
    const start = moment.utc(pred.startDate);
    const end = moment.utc(pred.endDate);
    const d = start.clone();
    let dayNum = 1;
    while (d.isSameOrBefore(end)) {
      const dateKey = d.format(DATE_DAY_FORMAT);
      if (!expandedCycleDays.some((e) => e.date === dateKey)) {
        expandedCycleDays.push({
          date: dateKey,
          dayNumber: dayNum,
          type: "predicted",
        });
      }
      d.add(1, "day");
      dayNum++;
    }
  }

  return expandedCycleDays;
};

export const buildPlanRowProps = (
  data,
  selectedWeek,
  monthKey,
  units,
  trackingItems
) => {
  const planExercises = groupBy(data.exercises, toDateKey);
  const weekDays = buildWeekDays(selectedWeek).map((d) => ({
    ...d,
    exercises: planExercises[d.dayKey] || [],
  }));

  return {
    weekDays,
    nutritionPlans: data.nutritionPlans || [],
    mealPlans: data.mealPlans || [],
    habits: data.habits || [],
    annualPhases: data.annualPhases || [],
    annualEvents: data.annualEvents || [],
    targetGoals: data.targetGoals || [],
    units,
    femaleCycleDays: buildFemaleCycleDaysWithPredictions(data, monthKey),
    // Tracking cards read Client Configuration directly (sections.tracking.items)
    // - PlanTrackingCard.js calls getTrackingForDay(trackingItems, dayKey, ...)
    // itself per day, same as the real Calendar's Plan view. Passed in from
    // AnnualPlanner.js (already loaded once for the whole page, not date-ranged
    // so it isn't refetched per week/month like the rest of this data).
    trackingItems: trackingItems || {},
    checkInDays: (data.checkInDays || []).map((d) =>
      moment.utc(String(d), "YYYYMMDD").format(DATE_DAY_FORMAT)
    ),
    cycles: data.cycles || [],
  };
};

// `athlete` (not `locals`) - LogWeekRow only ever reads `locals.athlete`
// (PhotosCarousel), so building the wrapper object here means the caller
// just passes the athlete doc it already has, instead of having to memoize
// its own `{ athlete }` literal to avoid invalidating the caller's memo on
// every render.
export const buildLogRowProps = (data, selectedWeek, units, athlete) => {
  const logExercises = groupBy(data.exercises, toDateKey);
  const logFoodLogs = groupBy(data.foodLogs, toDateKey);
  const logNutritionSummary = groupBy(data.nutritionSummary, toDateKey);
  const logHabitLogs = groupBy(data.habitLogs, toDateKey);
  const logDailyStats = groupBy(data.dailyStats, toDateKey);
  const logBodyMeasurements = groupBy(data.bodyMeasurements, toDateKey);
  const logProgressPhotos = groupBy(data.progressPhotos, toDateKey);
  const logMealPhotos = groupBy(data.mealPhotos, toDateKey);
  const logNutritionPlans = data.nutritionPlans || [];
  const prExerciseKeysByDay = buildPrExerciseKeysByDay(data.dayPersonalRecords);

  const weekDays = buildWeekDays(selectedWeek).map((d) => ({
    ...d,
    exercises: logExercises[d.dayKey] || [],
    foodLogs: logFoodLogs[d.dayKey] || [],
    nutritionSummary: (logNutritionSummary[d.dayKey] || [])[0] || null,
    nutritionPlans: logNutritionPlans,
    habitLogs: (logHabitLogs[d.dayKey] || [])[0] || null,
    dailyStat: (logDailyStats[d.dayKey] || [])[0] || null,
    bodyMeasurements: (logBodyMeasurements[d.dayKey] || [])[0] || null,
    progressPhotos: (logProgressPhotos[d.dayKey] || [])[0] || null,
    mealPhotos: (logMealPhotos[d.dayKey] || [])[0] || null,
    prExerciseKeys: prExerciseKeysByDay[d.dayKey] || new Set(),
  }));

  return {
    weekDays,
    units,
    locals: { athlete },
    repMaxPR: data.dayPersonalRecords?.repMaxPR,
    femaleCycleDays: expandLoggedFemaleCycleDays(
      getNormalizedLogs(
        data.femaleCycleDays || [],
        data.femaleCycleSettings || null
      ),
      DATE_DAY_FORMAT
    ),
  };
};
